import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Ban,
  Building2,
  Coins,
  Download,
  FileCheck2,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SupplierInvoiceCostDialog } from "@/components/admin/SupplierInvoiceCostDialog";
import { SupplierInvoicePdfAttachDialog } from "@/components/admin/SupplierInvoicePdfAttachDialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { formatDateTimeSP } from "@/lib/formatDate";
import { formatCnpj, formatPhone, formatQuantity } from "@/lib/format";
import { formatCpf } from "@/lib/cpf";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
// Runtime das abas internas do admin (keep-alive): `active` indica se ESTA aba está visível; `useTabDirty`
// registra "alterações não salvas" da aba. Fora de abas (testes) o default é active=true e no-op.
import { useTabActive, useTabDirty } from "@/layouts/adminTabs/tabRuntime";
import { createSupplier } from "@/lib/adminSuppliersApi";
import {
  applyMapSuggestions,
  confirmSupplierInvoice,
  createLink,
  getDfeBaseStatus,
  type DfeBaseStatus,
  discardSupplierInvoice,
  downloadSupplierInvoiceXml,
  downloadSupplierInvoicePdf,
  viewSupplierInvoicePdf,
  getSupplierPdfMeta,
  viewSupplierPdfOriginal,
  downloadSupplierPdfOriginal,
  type SupplierPdfCurrent,
  getBalances,
  getEmitterProfile,
  getLinkableReceivingItems,
  getNfCostPreview,
  applyNfCost,
  getLinks,
  getSupplierInvoice,
  invalidateSupplierInvoice,
  listSupplierInvoices,
  mapInvoiceItem,
  previewSupplierInvoice,
  revalidateSupplierInvoice,
  searchMaterials,
  searchProducts,
  type EmitterProfile,
  type ItemBalance,
  type LinkView,
  type MaterialOption,
  type ProductOption,
  type ReceivingItemOption,
  type SupplierInvoice,
  type SupplierInvoiceItem,
  type SupplierInvoiceListParams,
  type SupplierInvoiceSummary,
} from "@/lib/adminSupplierInvoicesApi";
import { SearchCombobox } from "./supplier-invoices/SearchCombobox";
import { SupplierInvoiceManifestationPanel } from "@/components/admin/SupplierInvoiceManifestationPanel";

const PAGE_SIZE = 20;
/** Sentinela do Select de situação: Radix não aceita value="" num item. */
const ALL_VALUE = "__all__";

type ImportStatus = SupplierInvoiceSummary["importStatus"];
type StatusFilter = "" | ImportStatus;

const STATUS_LABEL: Record<ImportStatus, string> = {
  PREVIA: "Prévia",
  CONFIRMADA: "Confirmada",
  DESCARTADA: "Descartada",
  INVALIDADA: "Invalidada",
};

// Rótulos do FILTRO de "Validação Naturexpress" (nossa conferência INTERNA = importStatus). PREVIA = não
// validada; CONFIRMADA = validada. É UM único eixo no backend (importStatus), SEPARADO da Manifestação SEFAZ
// (evento fiscal, filtro próprio quando D5 chegar). DESCARTADA/INVALIDADA = estados de ciclo preservados.
const STATUS_FILTER_LABEL: Record<ImportStatus, string> = {
  PREVIA: "Não validadas (prévia)",
  CONFIRMADA: "Validadas (confirmada)",
  DESCARTADA: "Descartadas",
  INVALIDADA: "Invalidadas",
};

const STATUS_BADGE: Record<ImportStatus, "default" | "secondary" | "outline" | "destructive"> = {
  PREVIA: "secondary",
  CONFIRMADA: "default",
  DESCARTADA: "outline",
  INVALIDADA: "destructive",
};

const DEST_LABEL: Record<string, string> = {
  MATERIA_PRIMA_IN_NATURA: "Produto IN NATURA",
  MATERIAL_EMBALAGEM: "Material de embalagem",
  NAO_MAPEADO: "Não mapeado",
};

type DestType = "produto" | "material";

interface ItemForm {
  destType: DestType;
  productId: string;
  materialId: string;
  /** Rótulo "código — nome (unidade)" do destino escolhido, para confirmação ao usuário. */
  destLabel: string;
  factor: string;
  receivingItemId: string;
  /** Rótulo do item de recebimento escolhido, para confirmação ao usuário. */
  receivingLabel: string;
  qty: string;
  /** Form pré-preenchido a partir da SUGESTÃO do fornecedor, aguardando confirmação explícita (Mapear). */
  suggested: boolean;
  /** "Atualizar o padrão deste fornecedor para as próximas notas" (desmarcado por padrão). */
  updateDefault: boolean;
}

const emptyForm = (): ItemForm => ({
  destType: "produto",
  productId: "",
  materialId: "",
  destLabel: "",
  factor: "",
  receivingItemId: "",
  receivingLabel: "",
  qty: "",
  suggested: false,
  updateDefault: false,
});

/** Normaliza o fator para exibição (remove zeros à direita do BigDecimal do backend: "10.000000" → "10"). */
const trimFactor = (v: string | null | undefined): string => {
  const t = (v ?? "").trim();
  if (!t) return "";
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? String(n) : t;
};

/** Rótulo legível do destino persistido de um item (código/nome vindos do backend), ou vazio se não mapeado. */
const destLabelOf = (item: SupplierInvoiceItem): string => {
  const isMaterial = item.destinationType === "MATERIAL_EMBALAGEM";
  if (isMaterial) {
    return item.mappedMaterialId ? (item.mappedMaterialName ?? "material") : "";
  }
  if (item.mappedProductId) {
    const base = `${item.mappedProductCode ?? "—"} — ${item.mappedProductName ?? "produto"}`;
    // Unidade de referência do produto (mappedProductUnit) — mantém o "(Kg)" no rótulo mesmo sem purchase_unit.
    const unit = item.mappedProductUnit ?? item.purchaseUnit;
    return unit ? `${base} (${unit})` : base;
  }
  return "";
};

/** Rótulo legível do destino de uma SUGESTÃO do fornecedor (código/nome/unidade memorizados). */
const suggestionLabel = (s: NonNullable<SupplierInvoiceItem["suggestion"]>): string => {
  if (s.destinationType === "MATERIAL_EMBALAGEM") {
    return s.materialName ?? "material";
  }
  const base = `${s.productCode ?? "—"} — ${s.productName ?? "produto"}`;
  return s.purchaseUnit ? `${base} (${s.purchaseUnit})` : base;
};

/**
 * Deriva o formulário inicial de um item. Se o item JÁ tem mapeamento efetivo, semeia dele. Se está PENDENTE mas
 * tem SUGESTÃO do fornecedor, pré-preenche a partir da sugestão (marcada como `suggested` — aguarda confirmação
 * explícita via "Mapear"). Sem mapeamento nem sugestão, começa vazio.
 */
const seedForm = (item: SupplierInvoiceItem): ItemForm => {
  const isMaterial = item.destinationType === "MATERIAL_EMBALAGEM";
  if (item.destinationType !== "NAO_MAPEADO" || !item.suggestion) {
    return {
      destType: isMaterial ? "material" : "produto",
      productId: item.mappedProductId ?? "",
      materialId: item.mappedMaterialId ?? "",
      destLabel: destLabelOf(item),
      factor: trimFactor(item.conversionFactor),
      receivingItemId: "",
      receivingLabel: "",
      qty: "",
      suggested: false,
      updateDefault: false,
    };
  }
  // Pré-preenchimento pela SUGESTÃO (padrão memorizado do fornecedor). Não é o mapeamento efetivo até confirmar.
  const s = item.suggestion;
  const sMaterial = s.destinationType === "MATERIAL_EMBALAGEM";
  return {
    destType: sMaterial ? "material" : "produto",
    productId: s.productId ?? "",
    materialId: s.materialId ?? "",
    destLabel: suggestionLabel(s),
    factor: trimFactor(s.conversionFactor),
    receivingItemId: "",
    receivingLabel: "",
    qty: "",
    suggested: true,
    updateDefault: false,
  };
};

const productLabel = (p: ProductOption) => `${p.code ?? "—"} — ${p.name}${p.unit ? ` (${p.unit})` : ""}`;
/** Unidade-alvo do destino escolhido — o que vem entre parênteses no rótulo (ex.: "… (Kg)" → "Kg"). */
const unitFromLabel = (label: string): string => {
  const m = /\(([^)]*)\)\s*$/.exec(label ?? "");
  return m ? m[1].trim() : "";
};
const materialLabel = (m: MaterialOption) => `${m.name}${m.unit ? ` (${m.unit})` : ""}`;
/** Rótulo de confirmação de um item de recebimento escolhido. */
const receivingLabelOf = (r: ReceivingItemOption) =>
  `${formatDateTimeSP(r.receivedAt)} · ${r.productName} · ${formatQuantity(r.quantity)} ${r.unit} · ${r.supplierName}`;

/** CNPJ/CPF formatado para leitura (o backend guarda só dígitos); outros formatos passam como vieram. */
const formatDocument = (doc: string | null | undefined): string => {
  if (!doc) return "—";
  const digits = doc.replace(/\D/g, "");
  if (digits.length === 14) return formatCnpj(digits);
  if (digits.length === 11) return formatCpf(digits);
  return doc;
};

/** Nome exibível do emitente: razão social do XML; sem ela (NF antiga), o fornecedor cadastrado; senão o documento. */
const emitterDisplayName = (e: SupplierInvoiceSummary["emitter"] | undefined, doc: string | null): string =>
  e?.name ?? e?.supplierName ?? formatDocument(doc);

const fmtMoney = (v: string | null | undefined): string => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : v;
};

const linksSummary = (links: LinkView[]) =>
  links
    .map((l) => `${formatQuantity(l.originalQuantity)}${l.originalUnit ?? ""}→${formatQuantity(l.convertedQuantity)}${l.destinationUnit ?? ""}`)
    .join("; ");

/** Aceita só XML (por extensão ou content-type): a NF-e é sempre um .xml. */
function isXmlFile(f: File): boolean {
  return (
    f.name.toLowerCase().endsWith(".xml") ||
    f.type === "text/xml" ||
    f.type === "application/xml"
  );
}

/**
 * Mensagem de erro CLARA para a falha de importação, específica por status — o 403/503 do backend costuma
 * vir sem corpo legível (Spring omite `message`), então a tela ficava silenciosa. Não classifica nem decide
 * nada de negócio: só traduz o status HTTP num texto acionável para o operador.
 */
function importErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) {
      return "Sem permissão para importar NF de fornecedor (FINANCEIRO.NF_ENTRADA.IMPORTAR). "
        + "Peça a um administrador para conceder o acesso.";
    }
    if (e.status === 503) {
      return "Módulo de NF indisponível no momento: a verificação de privacidade do storage não foi "
        + "concluída. Tente novamente em instantes ou contate o administrador.";
    }
    // 400/409/422 etc.: o backend costuma trazer um motivo legível — prioriza-o.
    if (e.message && e.message.trim()) return e.message;
  }
  return getApiErrorMessage(e, "Falha ao importar a NF-e");
}

/** Selo discreto para NF que veio da integração DF-e (SEFAZ), e não de upload manual. */
function SefazBadge() {
  return (
    <Badge variant="outline" className="text-[10px] uppercase" title="Importada automaticamente da SEFAZ (DF-e)">
      SEFAZ
    </Badge>
  );
}

function StatusBadge({ status }: { status: ImportStatus }) {
  return <Badge variant={STATUS_BADGE[status]}>{STATUS_LABEL[status]}</Badge>;
}

/** Importação documental da NF-e de fornecedor (entrada): XML → prévia → confirmação. Role-gated. */
export default function AdminSupplierInvoices() {
  // Upload manual único: o seletor de arquivo é disparado por um botão e a importação acontece na seleção.
  // Se o servidor exigir substituição explícita (409 por conteúdo diferente), o arquivo fica guardado para
  // oferecer a ação "Substituir prévia existente" — só nesse caso.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importConflictFile, setImportConflictFile] = useState<File | null>(null);
  const [invoice, setInvoice] = useState<SupplierInvoice | null>(null);
  const [busy, setBusy] = useState(false);
  // Aba ativa (keep-alive): quando a aba fica inativa, o diálogo FECHA (libera o scroll-lock/foco do modal
  // Radix, que é do documento) sem perder o estado — e REABRE ao voltar à aba, preservando o progresso.
  const tabActive = useTabActive();
  // Confirmação explícita do descarte de prévia (ação destrutiva de ciclo de vida).
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // Invalidação de importação confirmada: confirmação explícita + MOTIVO obrigatório (Entrega 2B).
  const [confirmInvalidateOpen, setConfirmInvalidateOpen] = useState(false);
  // Integração NF→custos: diálogo de prévia/aplicação do custo de compra (só NF confirmada).
  const [costOpen, setCostOpen] = useState(false);
  // NF→custos: por padrão o custo do catálogo é alterado (flag marcada). "Confirmar custo" abre um popup de
  // confirmação; o auto-apply aplica os itens limpos e manda os que precisam de conferência (divergência/custo
  // mais novo) para o diálogo detalhado.
  const [applyCostFlag, setApplyCostFlag] = useState(true);
  const [costConfirmOpen, setCostConfirmOpen] = useState(false);
  // PDF ORIGINAL do fornecedor (anexo documental): metadados do vigente + diálogo de anexo/substituição.
  const [pdfMeta, setPdfMeta] = useState<SupplierPdfCurrent | null>(null);
  const [pdfDialog, setPdfDialog] = useState<{ open: boolean; replace: boolean }>({ open: false, replace: false });
  const [invalidateReason, setInvalidateReason] = useState("");
  // Erro persistente da importação (inline), além do toast — o 403/503 não pode ser silencioso na tela.
  const [importError, setImportError] = useState<string | null>(null);
  const [balances, setBalances] = useState<ItemBalance[]>([]);
  const [links, setLinks] = useState<LinkView[]>([]);
  // Perfil cadastral do CNPJ emitente (Receita via BrasilAPI, cache no backend): carregado ao abrir o detalhe.
  const [profile, setProfile] = useState<EmitterProfile | null>(null);
  const [profileBusy, setProfileBusy] = useState(false);
  // "Cadastrar este emitente": formulário pré-preenchido com o que o XML (e a Receita, se houver) informou.
  const account = useApp((s) => s.account);
  const canCreateSupplier = hasPermission(account, "SUPRIMENTOS.FORNECEDOR.CRIAR");
  // Estado read-only RESUMIDO da base DF-e (só exibição); endpoint exige apenas VISUALIZAR, sem disparo fiscal.
  const [baseStatus, setBaseStatus] = useState<DfeBaseStatus | null>(null);
  const [supplierDraft, setSupplierDraft] = useState<SupplierDraft | null>(null);
  const [supplierSaving, setSupplierSaving] = useState(false);
  // formulários por item, chaveados por itemId
  const [forms, setForms] = useState<Record<string, ItemForm>>({});
  // lista/busca de NFs já importadas (consulta/reabertura após reload) + filtros reativos
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Período de EMISSÃO (data do XML), SEPARADO do período de importação acima.
  const [emittedFromDate, setEmittedFromDate] = useState("");
  const [emittedToDate, setEmittedToDate] = useState("");
  // Número da NF em campo DEDICADO (correspondência EXATA no backend), separado da busca ampla q.
  const [numberFilter, setNumberFilter] = useState("");
  // Faixa de valor sobre o TOTAL da NF (limites inclusivos). Texto pt-BR; normalizado no envio.
  const [minValueFilter, setMinValueFilter] = useState("");
  const [maxValueFilter, setMaxValueFilter] = useState("");
  // Filtro FISCAL de manifestação (SEFAZ), SEPARADO da Validação Naturexpress. Pesquisa LOCAL (sem SEFAZ).
  const [manifestationFilter, setManifestationFilter] = useState<"" | "KNOWN" | "NONE">("");
  const [mineOnly, setMineOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [listData, setListData] = useState<{
    content: SupplierInvoiceSummary[];
    totalPages: number;
    totalElements: number;
  }>({ content: [], totalPages: 0, totalElements: 0 });
  const [listBusy, setListBusy] = useState(false);
  // Sequência das consultas: com filtros reativos, uma resposta atrasada não pode sobrescrever a mais nova.
  const listSeq = useRef(0);

  /** Converte um limite de valor digitado (pt-BR) em número para a validação local da faixa; NaN se vazio/invalido. */
  const parseMoney = (v: string): number => {
    const t = v.trim();
    if (!t) return NaN;
    return Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t);
  };
  // Faixa de valor invertida (mínimo > máximo): bloqueia a consulta no FE com mensagem clara; o backend também
  // rejeita (400) como barreira final. Só considera inválido quando ambos são números válidos.
  const minNum = parseMoney(minValueFilter);
  const maxNum = parseMoney(maxValueFilter);
  const valueRangeInvalid = !Number.isNaN(minNum) && !Number.isNaN(maxNum) && minNum > maxNum;

  /** Monta os parâmetros da consulta a partir do estado atual de busca + filtros (para uma dada página). */
  const buildParams = (p: number): SupplierInvoiceListParams => ({
    q: query,
    status: statusFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
    emittedFrom: emittedFromDate || undefined,
    emittedTo: emittedToDate || undefined,
    number: numberFilter || undefined,
    minValue: minValueFilter || undefined,
    maxValue: maxValueFilter || undefined,
    manifestation: manifestationFilter || undefined,
    mine: mineOnly || undefined,
    page: p,
    size: PAGE_SIZE,
  });

  const applyPage = (res: Awaited<ReturnType<typeof listSupplierInvoices>>) => {
    setListData({ content: res.content, totalPages: res.totalPages, totalElements: res.totalElements });
    setPage(res.page);
  };

  const loadList = async (p: number) => {
    const seq = ++listSeq.current;
    setListBusy(true);
    try {
      const res = await listSupplierInvoices(buildParams(p));
      if (seq === listSeq.current) applyPage(res);
    } catch (e) {
      if (seq === listSeq.current) toast.error(getApiErrorMessage(e, "Falha ao carregar NFs"));
    } finally {
      if (seq === listSeq.current) setListBusy(false);
    }
  };

  /**
   * Atualiza a lista APÓS uma operação já persistida (importar/confirmar/descartar), PRESERVANDO busca,
   * filtros e página atuais. Se o refetch falhar, a operação continua bem-sucedida: mostra um aviso
   * ESPECÍFICO (não um erro da operação) e NÃO induz repetição — basta recarregar depois.
   */
  const refreshListAfterWrite = async () => {
    const seq = ++listSeq.current; // uma consulta mais nova (filtro alterado) prevalece sobre este refetch
    try {
      const res = await listSupplierInvoices(buildParams(page));
      if (seq === listSeq.current) applyPage(res);
    } catch {
      toast.warning("Operação salva. Não foi possível atualizar a lista agora — ajuste um filtro para recarregá-la.");
    }
  };

  // Estado read-only RESUMIDO da base DF-e. Esta tela NÃO dispara consulta fiscal: a coleta (manual em lotes ou
  // agendada) vive no Buscador de NF-e, exclusivo do ADMIN. Aqui só se PESQUISA a base local (exige VISUALIZAR).
  const loadBaseStatus = () => {
    getDfeBaseStatus().then(setBaseStatus).catch(() => setBaseStatus(null));
  };
  useEffect(() => { loadBaseStatus(); }, []);

  // Filtros reativos (padrão das demais telas): a primeira carga é imediata; mudanças esperam o usuário
  // parar de digitar/escolher antes de consultar, sempre voltando à primeira página.
  const firstLoad = useRef(true);
  useEffect(() => {
    // Faixa de valor invertida: não consulta (a mensagem inline orienta o ajuste); evita um 400 desnecessário.
    if (valueRangeInvalid) return;
    if (firstLoad.current) {
      firstLoad.current = false;
      void loadList(0);
      return;
    }
    const timer = setTimeout(() => void loadList(0), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, statusFilter, fromDate, toDate, emittedFromDate, emittedToDate, numberFilter, minValueFilter,
      maxValueFilter, manifestationFilter, mineOnly, valueRangeInvalid]);

  /**
   * Define a NF corrente e semeia o formulário de cada item a partir do mapeamento atual,
   * SEM sobrescrever formulários que o usuário já esteja editando (merge: só itens sem form local).
   * Chamado em todo caminho de setInvoice (upload, openInvoice, refetch pós-map, refetch pós-link).
   */
  const applyInvoice = (inv: SupplierInvoice) => {
    setInvoice(inv);
    setForms((prev) => {
      const next = { ...prev };
      for (const item of inv.items) {
        if (!next[item.id]) next[item.id] = seedForm(item);
      }
      return next;
    });
  };

  /** Fecha o detalhe e descarta o estado de edição (saldos, vínculos e formulários por item). */
  const closeInvoice = () => {
    setInvoice(null);
    setBalances([]);
    setLinks([]);
    setForms({});
    setProfile(null);
    // Mapeamentos feitos no detalhe alteram a pendência: recarrega a lista para o badge "Mapeamento pendente"
    // refletir o estado atual sem depender de recarregar a página (preserva filtro/página atuais).
    void refreshListAfterWrite();
  };

  /**
   * Consulta cadastral do emitente (só CNPJ). Falha nunca bloqueia o detalhe: o backend devolve o motivo.
   * Sequência: ao trocar de NF antes da resposta, a resposta atrasada da anterior é descartada.
   */
  const profileSeq = useRef(0);
  const loadProfile = async (accessKey: string, refresh: boolean) => {
    const seq = ++profileSeq.current;
    setProfileBusy(true);
    try {
      const p = await getEmitterProfile(accessKey, refresh);
      if (seq === profileSeq.current) setProfile(p);
    } catch (e) {
      if (seq === profileSeq.current) {
        setProfile({ available: false, error: getApiErrorMessage(e, "Falha ao consultar a Receita Federal") } as EmitterProfile);
      }
    } finally {
      if (seq === profileSeq.current) setProfileBusy(false);
    }
  };

  /** Abre o cadastro de fornecedor já preenchido: nome/documento/cidade do XML; telefone da Receita quando houver. */
  const openSupplierDraft = () => {
    if (!invoice) return;
    const e = invoice.emitter;
    const doc = invoice.emitterDocument ?? invoice.emitterCnpj ?? invoice.emitterCpf ?? "";
    const place = e?.city ? `${e.city}${e.uf ? `/${e.uf}` : ""}` : null;
    const notes = [
      `Criado a partir da NF ${invoice.number ?? "—"} (chave ${invoice.accessKey}).`,
      e?.ie ? `IE ${e.ie}.` : null,
      place ? `Município: ${place}.` : null,
      e?.tradeName && e.tradeName !== e.name ? `Nome fantasia: ${e.tradeName}.` : null,
    ].filter(Boolean).join(" ");
    setSupplierDraft({
      name: e?.name ?? profile?.razaoSocial ?? "",
      document: formatDocument(doc) === "—" ? "" : formatDocument(doc),
      phone: profile?.telefone ? formatPhone(profile.telefone) : "",
      notes,
    });
  };

  const saveSupplier = async () => {
    if (!invoice || !supplierDraft || !supplierDraft.name.trim()) return;
    setSupplierSaving(true);
    try {
      await createSupplier({
        name: supplierDraft.name.trim(),
        cnpj: supplierDraft.document.trim() || undefined,
        phone: supplierDraft.phone.trim() || undefined,
        notes: supplierDraft.notes.trim() || undefined,
      });
      toast.success("Fornecedor cadastrado.");
      setSupplierDraft(null);
      // O casamento é feito em leitura pelo documento: basta reler o detalhe para o card refletir o cadastro.
      applyInvoice(await getSupplierInvoice(invoice.accessKey));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao cadastrar o fornecedor"));
    } finally {
      setSupplierSaving(false);
    }
  };

  const openAccessKey = invoice?.accessKey ?? null;
  const openEmitterCnpj = invoice?.emitterCnpj ?? null;
  useEffect(() => {
    profileSeq.current++; // invalida qualquer consulta em voo da NF anterior
    setProfile(null);
    setProfileBusy(false);
    if (!openAccessKey || !openEmitterCnpj) return;
    void loadProfile(openAccessKey, false);
  }, [openAccessKey, openEmitterCnpj]);

  const formOf = (id: string): ItemForm => forms[id] ?? emptyForm();

  /** Normaliza um fator (string crua) para comparação numérica tolerante a vírgula/casas. */
  const normFactor = (v: string | null | undefined): string => {
    const t = (v ?? "").trim();
    if (!t) return "";
    const n = Number(t.replace(",", "."));
    return Number.isFinite(n) ? String(n) : t;
  };

  /**
   * O formulário de um item difere do mapeamento PERSISTIDO (invoice state)?
   * Compara destType/productId/materialId/fator. Usado para o indicador "não salvo".
   */
  const isFormDirty = (item: SupplierInvoiceItem, f: ItemForm): boolean => {
    const persisted = seedForm(item);
    return (
      f.destType !== persisted.destType ||
      f.productId !== persisted.productId ||
      f.materialId !== persisted.materialId ||
      normFactor(f.factor) !== normFactor(persisted.factor)
    );
  };
  const setFormFields = (id: string, patch: Partial<ItemForm>) =>
    setForms((f) => ({ ...f, [id]: { ...formOf(id), ...patch } }));
  const setFormField = (id: string, field: keyof ItemForm, value: string) =>
    setFormFields(id, { [field]: value });

  const refreshLinks = async (accessKey: string) => {
    try {
      setBalances(await getBalances(accessKey));
      setLinks(await getLinks(accessKey));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar saldos/vínculos"));
    }
  };

  // PDF do fornecedor: carrega o metadado (anexado?) ao abrir o detalhe.
  const reloadPdfMeta = () => {
    const key = invoice?.accessKey;
    if (key) getSupplierPdfMeta(key).then(setPdfMeta).catch(() => setPdfMeta(null));
  };
  useEffect(() => {
    const key = invoice?.accessKey;
    if (!key) { setPdfMeta(null); return; }
    let cancelled = false;
    getSupplierPdfMeta(key).then((m) => { if (!cancelled) setPdfMeta(m); }).catch(() => { if (!cancelled) setPdfMeta(null); });
    return () => { cancelled = true; };
  }, [invoice?.accessKey]);

  /**
   * Após vincular, o saldo restante do recebimento escolhido mudou. Reavalia as opções:
   * se a selecionada ainda é vinculável, atualiza o rótulo (novo saldo); se foi consumida
   * por completo, limpa a seleção para o usuário re-escolher. Não quebra em erro de API.
   */
  const refreshSelectedReceiving = async (accessKey: string, itemId: string, selectedId: string) => {
    if (!selectedId.trim()) return;
    try {
      const options = await getLinkableReceivingItems(accessKey, itemId);
      const stillThere = options.find((r) => r.receivingItemId === selectedId);
      if (stillThere) {
        setFormFields(itemId, { receivingLabel: receivingLabelOf(stillThere) });
      } else {
        setFormFields(itemId, { receivingItemId: "", receivingLabel: "" });
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao atualizar o recebimento selecionado"));
    }
  };

  const openInvoice = async (accessKey: string) => {
    setListBusy(true);
    try {
      const inv = await getSupplierInvoice(accessKey);
      applyInvoice(inv);
      await refreshLinks(accessKey);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao abrir a NF"));
    } finally {
      setListBusy(false);
    }
  };

  const mapItem = async (itemId: string) => {
    if (!invoice) return;
    const f = formOf(itemId);
    setBusy(true);
    try {
      if (f.destType === "material") {
        await mapInvoiceItem(itemId, {
          destinationType: "MATERIAL_EMBALAGEM",
          materialId: f.materialId.trim() || null,
          conversionFactor: f.factor.trim() ? Number(f.factor.replace(",", ".")) : null,
          purchaseUnit: null,
          updateSupplierDefault: f.updateDefault,
        });
      } else {
        await mapInvoiceItem(itemId, {
          destinationType: "MATERIA_PRIMA_IN_NATURA",
          productId: f.productId.trim() || null,
          conversionFactor: f.factor.trim() ? Number(f.factor.replace(",", ".")) : null,
          purchaseUnit: null,
          updateSupplierDefault: f.updateDefault,
        });
      }
      toast.success("Item mapeado.");
      // Recarrega o detalhe só após o sucesso para refletir destinationType na linha do item.
      const fresh = await getSupplierInvoice(invoice.accessKey);
      applyInvoice(fresh);
      // Re-semeia o form DESTE item a partir do estado persistido (limpa o marcador de "sugestão" e a flag de
      // atualizar padrão, já consumida). O merge do applyInvoice preserva forms existentes, então sincronizamos aqui.
      const mapped = fresh.items.find((i) => i.id === itemId);
      if (mapped) setForms((prev) => ({ ...prev, [itemId]: seedForm(mapped) }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao mapear"));
      // Resync: tanto um erro com status HTTP (o servidor NÃO alterou nada) quanto um erro de rede
      // (resultado DESCONHECIDO) exigem reconsultar o detalhe para saber o estado realmente persistido.
      // Se a reconsulta SUCEDER, re-semeia o form deste item (descarta os valores em edição rejeitados).
      // Se a PRÓPRIA reconsulta FALHAR, não afirmar "nada foi salvo" — avisar que o estado é incerto.
      try {
        const fresh = await getSupplierInvoice(invoice.accessKey);
        setInvoice(fresh);
        const freshItem = fresh.items.find((i) => i.id === itemId);
        if (freshItem) setForms((f) => ({ ...f, [itemId]: seedForm(freshItem) }));
      } catch {
        toast.error("Não foi possível confirmar o estado do mapeamento; recarregue a página.");
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Confirma EM LOTE as sugestões do fornecedor dos itens pendentes SEM divergência (uma única ação). Itens com
   * divergência/sem padrão são pulados (reportados) para revisão manual. Recarrega o detalhe após o sucesso.
   */
  const applySuggestions = async () => {
    if (!invoice) return;
    // SÓ itens com sugestão aplicável e SEM edição manual entram no lote — item editado (fator ≠ sugestão) fica
    // de fora e é confirmado individualmente com o valor digitado (sem ambiguidade de qual fator vale).
    const ids = invoice.items
      .filter((i) => !i.mappingComplete && i.suggestion?.applicable && !isFormDirty(i, formOf(i.id)))
      .map((i) => i.id);
    if (ids.length === 0) {
      toast.info("Nenhuma sugestão sem edição para confirmar em lote.");
      return;
    }
    setBusy(true);
    try {
      const res = await applyMapSuggestions(invoice.accessKey, ids);
      // Re-semeia os itens APLICADOS (agora completos) a partir do salvo; PRESERVA os forms pendentes/editados
      // (ex.: a Vagem editada em ×12 continua com o valor digitado para confirmação individual).
      const fresh = await getSupplierInvoice(invoice.accessKey);
      setInvoice(fresh);
      setForms((prev) => {
        const next = { ...prev };
        for (const i of fresh.items) {
          if (i.mappingComplete) next[i.id] = seedForm(i);
          else if (!next[i.id]) next[i.id] = seedForm(i);
        }
        return next;
      });
      if (res.applied > 0) {
        toast.success(`${res.applied} item(ns) confirmado(s) pela sugestão do fornecedor.`);
      }
      if (res.skipped > 0) {
        toast.warning(`${res.skipped} item(ns) exigem revisão manual.`);
      }
      if (res.applied === 0 && res.skipped === 0) {
        toast.info("Nenhuma sugestão aplicável no momento.");
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao confirmar as sugestões"));
    } finally {
      setBusy(false);
    }
  };

  const linkItem = async (itemId: string) => {
    if (!invoice) return;
    const f = formOf(itemId);
    const it = invoice.items.find((i) => i.id === itemId);
    setBusy(true);
    try {
      await createLink({
        invoiceItemId: itemId,
        receivingItemId: f.receivingItemId.trim(),
        // Vínculo INTEGRAL (1:1): a quantidade é sempre a do item inteiro da NF — o backend exige que a
        // convertida (qCom × fator) seja igual à quantidade do recebimento.
        originalQuantity: it ? Number(String(it.qCom).replace(",", ".")) : 0,
      });
      toast.success("Vínculo criado.");
      // Recarrega detalhe + saldos/vínculos só após o sucesso.
      applyInvoice(await getSupplierInvoice(invoice.accessKey));
      await refreshLinks(invoice.accessKey);
      // O saldo do recebimento selecionado mudou: reavalia a opção escolhida.
      await refreshSelectedReceiving(invoice.accessKey, itemId, f.receivingItemId);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Vínculo bloqueado"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Upload manual de um XML: importa na seleção do arquivo. Em 409 por "conteúdo diferente" (prévia existente
   * divergente), guarda o arquivo e a tela oferece "Substituir prévia existente" (replace explícito).
   */
  const uploadFile = async (f: File, replace: boolean) => {
    if (!isXmlFile(f)) {
      toast.error("Só aceita o XML da NF-e (.xml).");
      return;
    }
    setBusy(true);
    setImportError(null);
    setImportConflictFile(null);
    try {
      const result = await previewSupplierInvoice(f, replace);
      applyInvoice(result);
      setImportError(null);
      toast.success("Prévia importada.");
      await refreshListAfterWrite();
    } catch (e) {
      const msg = importErrorMessage(e);
      setImportError(msg);
      if (!replace && e instanceof ApiError && e.status === 409 && /substitui/i.test(msg)) {
        setImportConflictFile(f);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      // Envia APENAS a referência de revisão (hash) da prévia conferida — nunca o conteúdo do XML.
      const result = await confirmSupplierInvoice(invoice.accessKey, invoice.xmlSha256);
      applyInvoice(result);
      toast.success("NF-e confirmada.");
      await refreshListAfterWrite();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Confirmação bloqueada"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Aplica o custo da NF ao catálogo AUTOMATICAMENTE (flag marcada): pega a prévia e aplica os itens elegíveis
   * de uma vez. Mantém as travas de segurança — divergência de encargos e itens com custo mais novo NÃO são
   * sobrescritos em silêncio: caem no diálogo detalhado ("Revisar item a item") para conferência explícita.
   */
  const autoApplyCost = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      const preview = await getNfCostPreview(invoice.id);
      const eligible = preview.items.filter((it) => it.eligible && !it.alreadyApplied);
      if (eligible.length === 0) {
        toast.warning("Nenhum item elegível para custo — mapeie os itens a produtos/materiais primeiro.");
        return;
      }
      if (preview.chargeDivergence) {
        toast.warning("Há divergência de encargos nesta NF — revise item a item antes de aplicar o custo.");
        setCostOpen(true);
        return;
      }
      const res = await applyNfCost({
        invoiceId: invoice.id,
        acknowledgeChargeDivergence: false,
        items: eligible.map((it) => ({
          invoiceItemId: it.invoiceItemId,
          expectedProposedCost: it.proposedCost,
          expectedTargetVersion: it.currentVersion,
          confirmReplaceNewer: false, // custo mais novo NÃO é sobrescrito automaticamente
          expectedDestinationType: it.destinationType,
          expectedProductId: it.productId,
          expectedMaterialId: it.materialId,
          expectedReferenceUnit: it.referenceUnit,
          expectedConversionFactor: it.conversionFactor,
        })),
      });
      if (res.applied.length > 0) toast.success(`Custo aplicado em ${res.applied.length} item(ns).`);
      if (res.rejected.length > 0) {
        toast.warning(`${res.rejected.length} item(ns) precisam de conferência (divergência ou custo mais novo) — abra "Revisar item a item".`);
        setCostOpen(true);
      } else if (res.applied.length === 0) {
        toast.warning("Nenhum custo aplicado.");
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao aplicar o custo ao catálogo"));
    } finally {
      setBusy(false);
    }
  };

  /** Desfecho do popup de custo: aplica (flag marcada) ou apenas confirma sem alterar (flag desmarcada). */
  const onCostConfirm = async () => {
    setCostConfirmOpen(false);
    if (applyCostFlag) {
      await autoApplyCost();
    } else {
      toast.info("Custo do catálogo não alterado.");
    }
  };

  /**
   * Revalida a prévia: recomputa os eixos fiscais contra o truststore ATUAL, SEM confirmar. Reimportar o
   * mesmo XML é idempotente (não recalcula); esta ação existe para refletir cadeia/revogação provisionadas
   * APÓS a importação. Não confirma, não mapeia, não vincula; preserva filtro/página da lista.
   */
  const revalidate = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      const result = await revalidateSupplierInvoice(invoice.accessKey);
      applyInvoice(result);
      toast.success(
        result.trustedForConfirm
          ? "Prévia revalidada: confiança comprovada."
          : "Prévia revalidada (confiança ainda não comprovada).",
      );
      await refreshListAfterWrite();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao revalidar a prévia"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Descarta a PRÉVIA corrente (soft): só PREVIA, com confirmação explícita. Preserva XML/auditoria no
   * backend; a prévia sai da lista padrão. Após descartar, fecha o detalhe e atualiza a lista (preservando
   * busca/filtros/página). O backend gateia permissão e status (confirmada → 409).
   */
  const discard = async () => {
    if (!invoice) return;
    setConfirmDiscardOpen(false);
    setBusy(true);
    try {
      await discardSupplierInvoice(invoice.accessKey);
      toast.success("Prévia descartada.");
      closeInvoice(); // o detalhe deixa de refletir uma prévia que saiu da lista padrão
      await refreshListAfterWrite();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao descartar a prévia"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Invalida a importação CONFIRMADA corrente (Entrega 2B): confirmação explícita + MOTIVO obrigatório.
   * Estado terminal; o backend preserva XML/vínculos/saldos e bloqueia novos map/link. Após invalidar,
   * recarrega o detalhe (agora INVALIDADA) e a lista preservando os filtros.
   */
  const invalidate = async () => {
    if (!invoice || !invalidateReason.trim()) return;
    setConfirmInvalidateOpen(false);
    setBusy(true);
    try {
      const result = await invalidateSupplierInvoice(invoice.accessKey, invalidateReason.trim());
      applyInvoice(result);
      setInvalidateReason("");
      toast.success("Importação invalidada.");
      await refreshListAfterWrite();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao invalidar a importação"));
    } finally {
      setBusy(false);
    }
  };

  const total = listData.totalElements;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, total);
  const hasPrev = page > 0;
  const hasNext = page + 1 < listData.totalPages;

  // Pendência de mapeamento (indicador DERIVADO dos dados persistidos, nunca de estado de janela): itens da NF
  // CONFIRMADA ainda sem destino/fator. Enquanto houver pendência, o fechamento do detalhe fica BLOQUEADO — e,
  // como deriva do backend, ao reabrir a NF (ou após recarregar o navegador) o bloqueio é reavaliado do zero,
  // com o progresso já salvo preservado item a item.
  const pendingItems = invoice ? invoice.items.filter((i) => !i.mappingComplete) : [];
  const mappingBlocked = !!invoice && invoice.importStatus === "CONFIRMADA" && pendingItems.length > 0;
  // Enquanto houver mapeamento pendente aberto, a aba fica "suja": fechar a ABA pede confirmação (o progresso
  // não some ao trocar de aba, mas fechar a aba descartaria o contexto de trabalho). Limpa ao concluir/sair.
  useTabDirty(mappingBlocked);
  // Itens elegíveis à confirmação EM LOTE: pendentes, com sugestão aplicável E SEM edição manual (o form ainda
  // reflete a sugestão). Item editado ("não salvo", fator diferente da sugestão) fica DE FORA do lote — para não
  // haver dúvida sobre qual fator vale: o usuário confirma esse item individualmente com o valor que digitou.
  const bulkSuggestionItemIds = invoice
    ? invoice.items
        .filter((i) => !i.mappingComplete && i.suggestion?.applicable && !isFormDirty(i, formOf(i.id)))
        .map((i) => i.id)
    : [];
  const applicableSuggestionCount = bulkSuggestionItemIds.length;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Notas de entrada</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              XML da NF-e → prévia → confirmação → mapeamento e vínculo com recebimentos. As notas emitidas
              contra o CNPJ chegam sozinhas pela SEFAZ; o upload é só para um XML recebido fora desse fluxo.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Upload manual único: a importação acontece na seleção do arquivo. */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,text/xml,application/xml"
              className="hidden"
              aria-label="Arquivo XML da NF-e"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = ""; // permite reselecionar o mesmo arquivo
                if (f) void uploadFile(f, false);
              }}
            />
            <Button variant="secondary" className="gap-2" onClick={() => fileInputRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload manual (XML)
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_1fr_auto]">
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Search className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Buscar por chave, número, CPF/CNPJ ou nome"
              aria-label="Buscar NF"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Select
            value={statusFilter || ALL_VALUE}
            onValueChange={(v) => setStatusFilter(v === ALL_VALUE ? "" : (v as ImportStatus))}
          >
            <SelectTrigger className="bg-white/10 text-primary-foreground"
              aria-label="Filtrar por Validação Naturexpress (situação interna)"
              title="Validação Naturexpress é a NOSSA conferência interna (não validada = prévia; validada = confirmada) — NÃO é a Manifestação do destinatário na SEFAZ, que é um evento fiscal separado.">
              <SelectValue placeholder="Validação Naturexpress" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Validação Naturexpress: todas (ativas)</SelectItem>
              {(Object.keys(STATUS_FILTER_LABEL) as ImportStatus[]).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_FILTER_LABEL[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* O período filtra pela data de IMPORTAÇÃO. */}
          <DatePicker
            className="bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
            aria-label="Importada a partir de"
            value={fromDate}
            toDate={toDate || undefined}
            placeholder="Importada de"
            onChange={(next) => {
              setFromDate(next);
              if (next && toDate && next > toDate) setToDate(next);
            }}
          />
          <DatePicker
            className="bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
            aria-label="Importada até"
            value={toDate}
            fromDate={fromDate || undefined}
            placeholder="Importada até"
            onChange={(next) => {
              setToDate(next);
              if (next && fromDate && next < fromDate) setFromDate(next);
            }}
          />
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Switch
              id="nf-mine-only"
              checked={mineOnly}
              onCheckedChange={setMineOnly}
              className="data-[state=checked]:bg-white data-[state=unchecked]:bg-white/30 [&>span]:bg-primary"
            />
            <Label htmlFor="nf-mine-only" className="whitespace-nowrap text-sm text-primary-foreground">
              Subidas por mim
            </Label>
          </div>
        </div>

        {/* 2ª linha: período de EMISSÃO (data do XML), SEPARADO do período de importação da 1ª linha. */}
        <p className="mt-3 text-xs font-medium text-primary-foreground/80">
          Período de <strong>emissão</strong> (data do XML da nota) — diferente do período de{" "}
          <strong>importação</strong> (quando a nota entrou no sistema), filtrado acima.
        </p>
        <div className="mt-1 grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr]">
          <DatePicker
            className="bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
            aria-label="Emitida a partir de"
            value={emittedFromDate}
            toDate={emittedToDate || undefined}
            placeholder="Emitida de"
            onChange={(next) => {
              setEmittedFromDate(next);
              if (next && emittedToDate && next > emittedToDate) setEmittedToDate(next);
            }}
          />
          <DatePicker
            className="bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
            aria-label="Emitida até"
            value={emittedToDate}
            fromDate={emittedFromDate || undefined}
            placeholder="Emitida até"
            onChange={(next) => {
              setEmittedToDate(next);
              if (next && emittedFromDate && next < emittedFromDate) setEmittedFromDate(next);
            }}
          />
        </div>

        {/* 3ª linha: filtros DEDICADOS — número da NF (correspondência EXATA, separado da busca ampla) e faixa de
            valor sobre o TOTAL da NF (limites inclusivos). Tudo LOCAL (sem SEFAZ). */}
        <div className="mt-3 grid gap-3 md:grid-cols-3 xl:grid-cols-[2fr_1fr_1fr]">
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <FileText className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Número da NF (exato)"
              aria-label="Filtrar por número da NF (correspondência exata)"
              title="Correspondência EXATA pelo número da NF — para busca parcial use o campo de busca acima."
              inputMode="numeric"
              value={numberFilter}
              onChange={(e) => setNumberFilter(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Coins className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Valor mín. (R$)"
              aria-label="Valor mínimo da NF"
              title="Total mínimo da NF (inclusivo)."
              inputMode="decimal"
              value={minValueFilter}
              onChange={(e) => setMinValueFilter(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Coins className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Valor máx. (R$)"
              aria-label="Valor máximo da NF"
              title="Total máximo da NF (inclusivo)."
              inputMode="decimal"
              value={maxValueFilter}
              onChange={(e) => setMaxValueFilter(e.target.value)}
            />
          </div>
        </div>
        {valueRangeInvalid && (
          <p role="alert" className="mt-2 text-xs font-medium text-amber-200">
            Faixa de valor inválida: o valor mínimo é maior que o máximo. Ajuste os limites para pesquisar.
          </p>
        )}

        {/* 4ª linha: filtro FISCAL de Manifestação do destinatário (SEFAZ) — SEPARADO da Validação Naturexpress.
            Pesquisa LOCAL (sem consultar a SEFAZ). "Conhecida" = registrada por esta aplicação (não é o histórico
            fiscal completo); distingue "sem manifestação conhecida" de "sem manifestação comprovada". */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-primary-foreground/80">Manifestação (SEFAZ):</span>
          <Select
            value={manifestationFilter || ALL_VALUE}
            onValueChange={(v) => setManifestationFilter(v === ALL_VALUE ? "" : (v as "KNOWN" | "NONE"))}
          >
            <SelectTrigger className="w-72 bg-white/10 text-primary-foreground"
              aria-label="Filtrar por manifestação fiscal (SEFAZ)"
              title="Evento fiscal da SEFAZ, separado da Validação Naturexpress (conferência interna). Busca local.">
              <SelectValue placeholder="Manifestação (SEFAZ)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Manifestação: todas</SelectItem>
              <SelectItem value="KNOWN">Com manifestação conhecida</SelectItem>
              <SelectItem value="NONE">Sem manifestação conhecida</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Esta tela PESQUISA a base local (sem consultar a SEFAZ). A COLETA das notas (manual em lotes ou
          agendada) é feita no Buscador de NF-e, exclusivo do ADMIN. Indicador read-only de atualização da base. */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
        <span>
          Busca <strong>local</strong> na base já coletada — não consulta a SEFAZ. A coleta das notas é feita no
          Buscador de NF-e (ADMIN).
        </span>
        {baseStatus && (
          <span className="whitespace-nowrap">
            Última atualização da base:{" "}
            <strong>{baseStatus.lastUpdateAt ? formatDateTimeSP(baseStatus.lastUpdateAt) : "—"}</strong>
            {" · "}posições de NSU pendentes:{" "}
            <strong>{baseStatus.pendingPositions ?? "—"}</strong>
          </span>
        )}
      </div>

      {importError && (
        <div
          role="alert"
          className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <div className="flex-1">{importError}</div>
          <div className="flex items-center gap-2">
            {importConflictFile && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => void uploadFile(importConflictFile, true)}>
                Substituir prévia existente por {importConflictFile.name}
              </Button>
            )}
            <Button size="icon" variant="ghost" aria-label="Fechar aviso" onClick={() => { setImportError(null); setImportConflictFile(null); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-card shadow-sm">
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <h2 className="text-sm font-semibold">NFs importadas</h2>
          {listBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Carregando" />}
        </div>
        {listData.content.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
            {listBusy ? (
              <Loader2 className="h-8 w-8 animate-spin opacity-50" />
            ) : (
              <>
                <FileText className="h-10 w-10 opacity-30" />
                <p className="text-sm">Nenhuma NF encontrada.</p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Emitente</TableHead>
                  <TableHead>NF</TableHead>
                  <TableHead className="text-center">Itens</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>Emissão</TableHead>
                  <TableHead>Importada em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listData.content.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => void openInvoice(row.accessKey)}
                  >
                    <TableCell>
                      <div className="font-medium">{emitterDisplayName(row.emitter, row.emitterDocument ?? row.emitterCnpj)}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDocument(row.emitterDocument ?? row.emitterCnpj)}
                        {row.emitter?.city && ` · ${row.emitter.city}${row.emitter.uf ? `/${row.emitter.uf}` : ""}`}
                      </div>
                      {row.emitter?.supplierName ? (
                        <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary">
                          <Building2 className="h-3 w-3" /> {row.emitter.supplierName}
                        </div>
                      ) : (
                        <div className="mt-0.5 text-xs text-muted-foreground">Fornecedor não cadastrado</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 whitespace-nowrap font-medium">
                        {row.number ?? "—"}
                        {row.series && <span className="text-xs font-normal text-muted-foreground">série {row.series}</span>}
                        {row.origin === "SEFAZ_DFE" && <SefazBadge />}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground" title="Chave de acesso">
                        {row.accessKey}
                      </div>
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{row.itemCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(row.totalNfe)}</TableCell>
                    <TableCell>
                      <StatusBadge status={row.importStatus} />
                      {row.mappingPending && (
                        <div className="mt-1">
                          <Badge variant="outline" className="border-warning/60 text-warning">
                            Mapeamento pendente ({row.pendingItemCount})
                          </Badge>
                        </div>
                      )}
                      {row.confirmedByName && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          aceita por {row.confirmedByName}
                          <br />{formatDateTimeSP(row.confirmedAt)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTimeSP(row.emittedAt)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTimeSP(row.importedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Mostrando {showingFrom}-{showingTo} de {total}
            </div>
            <Pagination className="mx-0 w-auto justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    aria-disabled={!hasPrev}
                    tabIndex={hasPrev ? 0 : -1}
                    onClick={(e) => { e.preventDefault(); if (hasPrev && !listBusy) void loadList(page - 1); }}
                    className={!hasPrev ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    aria-disabled={!hasNext}
                    tabIndex={hasNext ? 0 : -1}
                    onClick={(e) => { e.preventDefault(); if (hasNext && !listBusy) void loadList(page + 1); }}
                    className={!hasNext ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {/* Não fecha no meio de uma operação: a resposta reaplicaria a NF e o diálogo "voltaria" sozinho. */}
      {/* open gateado pela aba ativa: ao sair da aba o modal fecha (libera scroll-lock/foco do documento) sem
          limpar `invoice` — reabre ao voltar, com o progresso intacto. O fechamento por aba NÃO passa pelo
          bloqueio (não é o usuário desistindo); só o fechamento do USUÁRIO na aba ativa é bloqueado enquanto
          pendente. Abrir o Nexo não troca de aba, então o diálogo permanece aberto (o Nexo coexiste com o modal). */}
      <Dialog
        open={invoice !== null && tabActive}
        onOpenChange={(o) => { if (!o && tabActive && !busy && !mappingBlocked) closeInvoice(); }}
      >
        <DialogContent
          className="max-h-[90vh] max-w-4xl overflow-y-auto"
          hideCloseButton={mappingBlocked}
          onInteractOutside={(e) => { if (mappingBlocked) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (mappingBlocked) e.preventDefault(); }}
        >
          {invoice && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  NF {invoice.number ?? "—"}{invoice.series ? ` · série ${invoice.series}` : ""}
                  <StatusBadge status={invoice.importStatus} />
                  {invoice.origin === "SEFAZ_DFE" && <SefazBadge />}
                </DialogTitle>
                <DialogDescription className="font-mono text-xs">Chave {invoice.accessKey}</DialogDescription>
              </DialogHeader>

              <div className="flex flex-col gap-4">
                {mappingBlocked && (
                  <div role="status" className="rounded-xl border border-warning/60 bg-warning/15 p-4 text-sm">
                    <div className="flex items-center gap-2 font-semibold">
                      <ShieldAlert className="h-4 w-4 text-warning" /> Mapeamento pendente
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {pendingItems.length} de {invoice.items.length} {invoice.items.length === 1 ? "item ainda precisa" : "itens ainda precisam"} de destino
                      e conversão. Conclua o mapeamento de todos os itens — inclusive embalagens e materiais de uso
                      interno — antes de fechar. O progresso já salvo é preservado se você sair e voltar depois.
                    </p>
                    {applicableSuggestionCount > 0 && (
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={() => void applySuggestions()}
                        disabled={busy}
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Confirmar sugestões do fornecedor ({applicableSuggestionCount})
                      </Button>
                    )}
                  </div>
                )}
                <EmitterCard
                  invoice={invoice}
                  profile={profile}
                  profileBusy={profileBusy}
                  onRefresh={() => void loadProfile(invoice.accessKey, true)}
                  onCreateSupplier={canCreateSupplier ? openSupplierDraft : undefined}
                />

                {/* Manifestação do destinatário (NT 2020.001): fiscal, EXCLUSIVA do ADMIN, separada da Validação
                    Naturexpress. Atalho no detalhe (SEFAZ ou XML importado manualmente); o backend valida a
                    elegibilidade (destinatário/estabelecimento/ambiente) e responde "não elegível" quando não couber. */}
                {account?.role === "ADMIN" && (
                  <SupplierInvoiceManifestationPanel accessKey={invoice.accessKey} />
                )}

                <div className="grid gap-3 rounded-xl border p-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="Emissão" value={formatDateTimeSP(invoice.emittedAt)} />
                  <Field label="Total da NF" value={fmtMoney(invoice.totalNfe)} />
                  <Field label="Frete" value={fmtMoney(invoice.totalFreight)} />
                  <Field label="Desconto" value={fmtMoney(invoice.totalDiscount)} />
                  <Field label="Origem" value={<span>Subida por {invoice.importedByName ?? "—"}</span>} />
                  {invoice.importStatus === "CONFIRMADA" && (
                    <Field
                      label="Aceite"
                      value={<span>Aceita por {invoice.confirmedByName ?? "—"} em {formatDateTimeSP(invoice.confirmedAt)}</span>}
                    />
                  )}
                </div>

                {invoice.importStatus === "INVALIDADA" && (
                  <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                    <div className="inline-flex items-center gap-1 font-medium">
                      <Ban className="h-4 w-4" /> Importação invalidada em {formatDateTimeSP(invoice.invalidatedAt)}.
                    </div>
                    {invoice.invalidationReason && <div className="mt-1 text-xs">Motivo: {invoice.invalidationReason}</div>}
                    <div className="mt-1 text-xs text-muted-foreground">
                      Vínculos e XML preservados. Invalidar no sistema não cancela a NF na SEFAZ.
                    </div>
                  </div>
                )}

                {/* Eixos fiscais SEPARADOS — nunca colapsados */}
                <div className="rounded-xl border p-4">
                  <div className="mb-3 text-sm font-semibold">Diagnóstico fiscal</div>
                  <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
                    <Axis label="Integridade" ok={invoice.integrityOk} />
                    <Axis label="Assinatura" ok={invoice.signatureOk} />
                    <Axis label="Cadeia confiável" ok={invoice.chainTrusted} />
                    <div className="rounded-lg border p-2">
                      <div className="text-muted-foreground">Revogação</div>
                      <div className="font-semibold">{invoice.revocation}</div>
                    </div>
                  </div>
                  <div className={`mt-3 rounded-lg p-3 text-sm ${invoice.trustedForConfirm ? "bg-primary/10" : "bg-warning/15"}`}>
                    <span className="inline-flex items-center gap-1">
                      {invoice.trustedForConfirm ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
                      {invoice.statusLabel}
                    </span>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Situação na SEFAZ não consultada por este fluxo; protocolo presente ≠ situação atual.
                    </div>
                  </div>
                  {invoice.importStatus === "PREVIA" && !invoice.trustedForConfirm && (
                    <div className="mt-2 text-xs text-warning" role="note">
                      Confirmação indisponível: {invoice.statusLabel}. Se a cadeia/revogação foi provisionada
                      após a importação, use “Revalidar prévia” para recomputar o diagnóstico sem confirmar.
                    </div>
                  )}
                </div>

                {invoice.items.length > 0 && (
                  <div className="rounded-xl border">
                    <div className="border-b px-4 py-3 text-sm font-semibold">Itens ({invoice.items.length})</div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">#</TableHead>
                            <TableHead>Cód.</TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead>NCM / CFOP</TableHead>
                            <TableHead className="text-right">Qtd</TableHead>
                            <TableHead className="text-right">Vl. unit.</TableHead>
                            <TableHead className="text-right">Vl. prod.</TableHead>
                            <TableHead>Destino</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoice.items.map((it) => (
                            <TableRow key={it.id}>
                              <TableCell className="tabular-nums">{it.nItem}</TableCell>
                              <TableCell className="font-mono text-xs">{it.supplierCode ?? "—"}</TableCell>
                              <TableCell>{it.description}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{it.ncm ?? "—"} / {it.cfop ?? "—"}</TableCell>
                              <TableCell className="whitespace-nowrap text-right tabular-nums">{formatQuantity(it.qCom)} {it.uCom}</TableCell>
                              <TableCell className="text-right tabular-nums">{fmtMoney(it.vUnCom)}</TableCell>
                              <TableCell className="text-right tabular-nums">{fmtMoney(it.vProd)}</TableCell>
                              <TableCell className="text-xs">
                                <div>{DEST_LABEL[it.destinationType] ?? it.destinationType}</div>
                                {destLabelOf(it) && <div className="text-muted-foreground">{destLabelOf(it)}</div>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Mapeamento + vínculo — liberados SOMENTE após confirmar a NF-e (a prévia é diagnóstica).
                    O backend também bloqueia (422) map/link enquanto não CONFIRMADA — este gate é só de UX. */}
                {invoice.importStatus === "INVALIDADA" ? (
                  /* NF invalidada: vínculos e saldos em SOMENTE LEITURA (sem mapear/vincular). */
                  <div className="space-y-2 rounded-xl border p-4">
                    <div className="text-sm font-semibold">Vínculos e saldos (somente leitura)</div>
                    <p className="text-xs text-muted-foreground">
                      Importação invalidada: mapeamento e vínculos desabilitados; os existentes são preservados (auditoria).
                    </p>
                    {invoice.items.map((it) => {
                      const bal = balances.find((b) => b.invoiceItemId === it.id);
                      return (
                        <div key={it.id} className="rounded-lg border p-2 text-xs">
                          <div className="font-medium">#{it.nItem} {it.description} ({it.supplierCode})</div>
                          {bal && (
                            <div className="text-muted-foreground">saldo: {formatQuantity(bal.remainingQuantity)}/{formatQuantity(bal.nfQuantity)}</div>
                          )}
                        </div>
                      );
                    })}
                    {links.length > 0 ? (
                      <div className="text-xs text-muted-foreground">{links.length} vínculo(s): {linksSummary(links)}</div>
                    ) : (
                      <div className="text-xs text-muted-foreground">Sem vínculos registrados.</div>
                    )}
                  </div>
                ) : invoice.importStatus !== "CONFIRMADA" ? (
                  <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                    Mapeamento e vínculos ficam disponíveis após confirmar a NF-e.
                  </div>
                ) : (
                  <div className="space-y-3 rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">Mapeamento e vínculos</div>
                      <Button size="sm" variant="ghost" onClick={() => void refreshLinks(invoice.accessKey)}>
                        <RefreshCw className="h-4 w-4" /> Atualizar saldos
                      </Button>
                    </div>
                    {invoice.items.map((it) => {
                      const f = formOf(it.id);
                      const bal = balances.find((b) => b.invoiceItemId === it.id);
                      const dirty = isFormDirty(it, f);
                      // Vínculo só faz sentido quando o item JÁ está mapeado para um produto E salvo (o backend
                      // resolve os recebimentos vinculáveis pelo mapeamento CONGELADO no item, não pela seleção
                      // em edição). Enquanto não salvo, o seletor fica oculto com uma dica — "sem recebimento
                      // vinculável" não deve ser confundido com "mapeie primeiro".
                      const itemMappedToProduct =
                        it.destinationType === "MATERIA_PRIMA_IN_NATURA" && !!it.mappedProductId;
                      // Vínculo 1:1 já existente deste item — mostrado para CONSULTA (não some da tela).
                      const existingLink = links.find((l) => l.invoiceItemId === it.id);
                      const canLink = itemMappedToProduct && !dirty;
                      // Sugestão do fornecedor exibida só enquanto o item está PENDENTE (o editor já vem
                      // pré-preenchido por ela); some assim que o mapeamento efetivo é confirmado.
                      const suggestion = !it.mappingComplete ? it.suggestion : null;
                      // Rótulo do botão de salvar o mapeamento deste item: "Confirmar" quando o editor veio de uma
                      // sugestão, "Mapear" caso contrário. Os avisos reutilizam este mesmo rótulo (consistência).
                      const actionLabel = f.suggested ? "Confirmar" : "Mapear";
                      return (
                        <div key={it.id} className="space-y-3 rounded-lg border p-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-medium">#{it.nItem} {it.description} ({it.supplierCode})</div>
                            <div className="flex items-center gap-2">
                              {suggestion && (
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${suggestion.applicable ? "bg-primary/10 text-primary" : "bg-warning/20 text-warning"}`}
                                  title={suggestion.applicable
                                    ? "Padrão memorizado deste fornecedor — confira e confirme."
                                    : (suggestion.reviewReason ?? "Sugestão exige revisão.")}
                                >
                                  {suggestion.applicable ? "Sugestão do fornecedor" : "Sugestão — revisar"}
                                </span>
                              )}
                              {dirty && (
                                <span className="text-xs font-medium text-warning" aria-label="mapeamento não salvo">não salvo</span>
                              )}
                            </div>
                          </div>
                          <div className="grid gap-2 md:grid-cols-[1fr_2fr_6rem_auto]">
                            <Select
                              value={f.destType}
                              onValueChange={(v) =>
                                setFormFields(it.id, { destType: v as DestType, productId: "", materialId: "", destLabel: "" })
                              }
                            >
                              <SelectTrigger aria-label="Tipo de destino">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="produto">Produto IN NATURA</SelectItem>
                                <SelectItem value="material">Material de embalagem</SelectItem>
                              </SelectContent>
                            </Select>
                            {f.destType === "produto" ? (
                              <SearchCombobox<ProductOption>
                                label="Produto IN NATURA"
                                placeholder="Escolher produto"
                                searchPlaceholder="Buscar produto (nome/código)"
                                emptyText="Nenhum produto encontrado."
                                errorText="Falha ao buscar produtos"
                                mode="search"
                                value={f.destLabel}
                                fetchOptions={searchProducts}
                                keyOf={(p) => p.id}
                                labelOf={productLabel}
                                onSelect={(p) => setFormFields(it.id, { productId: p.id, destLabel: productLabel(p) })}
                              />
                            ) : (
                              <SearchCombobox<MaterialOption>
                                label="Material de embalagem"
                                placeholder="Escolher material"
                                searchPlaceholder="Buscar material"
                                emptyText="Nenhum material encontrado."
                                errorText="Falha ao buscar materiais"
                                mode="search"
                                value={f.destLabel}
                                fetchOptions={searchMaterials}
                                keyOf={(m) => m.id}
                                labelOf={materialLabel}
                                onSelect={(m) => setFormFields(it.id, { materialId: m.id, destLabel: materialLabel(m) })}
                              />
                            )}
                            <Input
                              placeholder="fator"
                              aria-label="Fator de conversão: quantas unidades do produto há em 1 unidade da NF"
                              inputMode="decimal"
                              value={f.factor}
                              onChange={(e) => setFormField(it.id, "factor", e.target.value)}
                            />
                            <Button variant="outline" onClick={() => void mapItem(it.id)} disabled={busy}>
                              {actionLabel}
                            </Button>
                          </div>
                          {/* Conversão autoexplicativa: usa as unidades REAIS do destino (produto OU material) e
                              mostra a prévia (qtd da NF × fator). Não fixa KG — o alvo vem da unidade do destino. */}
                          <FactorHint
                            uCom={it.uCom}
                            targetUnit={unitFromLabel(f.destLabel)}
                            targetFallback={f.destType === "material" ? "un. do material" : "un. do produto"}
                            factor={f.factor}
                            qCom={it.qCom}
                          />
                          {suggestion && !suggestion.applicable && suggestion.reviewReason && (
                            <p className="text-xs text-warning" role="note">{suggestion.reviewReason}</p>
                          )}
                          {suggestion && (
                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5"
                                checked={f.updateDefault}
                                onChange={(e) => setFormFields(it.id, { updateDefault: e.target.checked })}
                              />
                              Atualizar o padrão deste fornecedor para as próximas notas
                            </label>
                          )}
                          {existingLink ? (
                            /* Vínculo 1:1 já feito — read-only, para CONSULTA (não some da tela). O recebimento
                               continua fora da lista de novos vínculos (regra de não-reassociação). */
                            <div className="rounded-md border bg-muted/30 p-2 text-xs">
                              <div className="font-medium text-foreground">Vinculado a um recebimento</div>
                              <div className="mt-0.5 text-muted-foreground">
                                {existingLink.receivedAt ? formatDateTimeSP(existingLink.receivedAt) : "—"}
                                {existingLink.productName ? ` · ${existingLink.productName}` : ""}
                                {existingLink.receivingQuantity
                                  ? ` · ${formatQuantity(existingLink.receivingQuantity)} ${existingLink.receivingUnit ?? ""}`
                                  : ""}
                                {existingLink.supplierName ? ` · ${existingLink.supplierName}` : ""}
                              </div>
                              <div className="mt-0.5 text-muted-foreground">
                                {formatQuantity(existingLink.originalQuantity)} {existingLink.originalUnit ?? ""} →{" "}
                                {formatQuantity(existingLink.convertedQuantity)} {existingLink.destinationUnit ?? ""}
                              </div>
                            </div>
                          ) : f.destType === "material" ? (
                            <div className="text-xs text-muted-foreground">
                              Materiais de embalagem não são vinculáveis a recebimento — apenas mapeamento.
                            </div>
                          ) : !canLink ? (
                            <div className="rounded-md border border-dashed bg-muted/30 p-2 text-xs text-muted-foreground">
                              {dirty
                                ? `Há alterações não salvas no mapeamento — clique em ${actionLabel} para salvar antes de vincular o recebimento.`
                                : `Mapeie o produto (botão ${actionLabel}) antes de vincular o recebimento. Só então os recebimentos deste produto aparecem.`}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="grid items-center gap-2 md:grid-cols-[1fr_2fr_auto]">
                                <div className="text-xs text-muted-foreground">
                                  Vincular a recebimento
                                  {bal && <div>saldo: {formatQuantity(bal.remainingQuantity)}/{formatQuantity(bal.nfQuantity)}</div>}
                                </div>
                                <SearchCombobox<ReceivingItemOption>
                                  label="Item de recebimento"
                                  placeholder="Escolher recebimento"
                                  searchPlaceholder="Filtrar recebimentos"
                                  emptyText="Nenhum recebimento deste produto disponível (não vinculado)."
                                  errorText="Falha ao carregar recebimentos vinculáveis"
                                  mode="load"
                                  value={f.receivingLabel}
                                  fetchOptions={() => getLinkableReceivingItems(invoice.accessKey, it.id)}
                                  keyOf={(r) => r.receivingItemId}
                                  labelOf={receivingLabelOf}
                                  onSelect={(r) =>
                                    setFormFields(it.id, { receivingItemId: r.receivingItemId, receivingLabel: receivingLabelOf(r) })
                                  }
                                />
                                <Button variant="outline" onClick={() => void linkItem(it.id)} disabled={busy}>Vincular</Button>
                              </div>
                              {/* Vínculo INTEGRAL (1:1): a quantidade é sempre a do item inteiro; o recebimento deve ter
                                  exatamente a quantidade convertida (qCom × fator). */}
                              <ExpectedReceivingHint uCom={it.uCom} qCom={it.qCom} factor={f.factor} targetUnit={unitFromLabel(f.destLabel)} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {links.length > 0 && (
                      <div className="text-xs text-muted-foreground">{links.length} vínculo(s): {linksSummary(links)}</div>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2 sm:justify-between">
                <div className="flex flex-col gap-2">
                  {/* PDF ORIGINAL do fornecedor (documento recebido; anexo manual). */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium">
                      {pdfMeta?.attached ? "PDF do fornecedor — anexado" : "PDF do fornecedor não anexado"}
                    </span>
                    {pdfMeta?.attached ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => void viewSupplierPdfOriginal(invoice.accessKey)
                          .catch((e) => toast.error(e instanceof Error ? e.message : "Falha ao abrir"))}>
                          <FileText className="h-4 w-4" /> Visualizar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void downloadSupplierPdfOriginal(invoice.accessKey)
                          .catch((e) => toast.error(e instanceof Error ? e.message : "Falha ao baixar"))}>
                          <Download className="h-4 w-4" /> Baixar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setPdfDialog({ open: true, replace: true })}>
                          <Upload className="h-4 w-4" /> Substituir
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setPdfDialog({ open: true, replace: false })}>
                        <Upload className="h-4 w-4" /> Anexar PDF
                      </Button>
                    )}
                  </div>
                  {/* DANFE gerado do XML (representação interna) + XML original — rótulo em linha própria e os
                      botões Visualizar/Baixar lado a lado. */}
                  <div className="flex flex-col gap-1 text-muted-foreground">
                    <span className="text-xs">
                      DANFE gerado do XML (representação interna — não é o DANFE oficial nem o PDF do fornecedor):
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => void viewSupplierInvoicePdf(invoice.accessKey)
                        .catch((e) => toast.error(e instanceof Error ? e.message : "PDF não disponível"))}>
                        <FileText className="h-4 w-4" /> Visualizar DANFE
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void downloadSupplierInvoicePdf(invoice.accessKey)
                        .catch((e) => toast.error(e instanceof Error ? e.message : "PDF não disponível"))}>
                        <Download className="h-4 w-4" /> Baixar DANFE
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void downloadSupplierInvoiceXml(invoice.accessKey)}>
                        <Download className="h-4 w-4" /> Baixar XML original
                      </Button>
                    </div>
                  </div>
                  <SupplierInvoicePdfAttachDialog
                    accessKey={invoice.accessKey}
                    number={invoice.number}
                    supplier={invoice.emitter?.name ?? invoice.emitterDocument}
                    replace={pdfDialog.replace}
                    open={pdfDialog.open}
                    onOpenChange={(o) => setPdfDialog((p) => ({ ...p, open: o }))}
                    onAttached={reloadPdfMeta}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {invoice.importStatus === "PREVIA" && (
                    <>
                      {/* Recomputa os eixos fiscais contra o truststore atual SEM confirmar — útil quando a
                          cadeia/revogação foi provisionada depois de a prévia ter sido importada. */}
                      <Button variant="outline" onClick={() => void revalidate()} disabled={busy}>
                        <RefreshCw className="h-4 w-4" /> Revalidar prévia
                      </Button>
                      {/* Descarte (soft) com confirmação explícita; só PREVIA. */}
                      <Button variant="ghost" className="text-destructive hover:text-destructive"
                        onClick={() => setConfirmDiscardOpen(true)} disabled={busy}>
                        <Trash2 className="h-4 w-4" /> Descartar prévia
                      </Button>
                      {/* UX: desabilita quando a confirmação SERÁ recusada (confiança/revogação não comprovadas).
                          O backend continua validando tudo no confirm — isto só evita um clique fadado a 422. */}
                      <Button onClick={() => void confirm()} disabled={busy || !invoice.trustedForConfirm}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />} Confirmar NF-e
                      </Button>
                    </>
                  )}
                  {invoice.importStatus === "CONFIRMADA" && (
                    <>
                      {/* NF→custos: por PADRÃO altera o custo do catálogo (flag marcada). "Confirmar custo" abre
                          um popup de confirmação; o auto-apply aplica os itens limpos e manda os que precisam de
                          conferência (divergência / custo mais novo) para "Revisar item a item". O backend gateia
                          a permissão APLICAR_CUSTO e recalcula tudo. */}
                      <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5">
                        <Switch id="apply-cost-flag" checked={applyCostFlag} onCheckedChange={setApplyCostFlag} />
                        <Label htmlFor="apply-cost-flag" className="whitespace-nowrap text-sm">Aplicar custo ao catálogo</Label>
                      </div>
                      <Button variant="outline" onClick={() => setCostConfirmOpen(true)} disabled={busy}>
                        <Coins className="h-4 w-4" /> Confirmar custo
                      </Button>
                      <Button variant="ghost" onClick={() => setCostOpen(true)} disabled={busy}>
                        Revisar item a item
                      </Button>
                      <SupplierInvoiceCostDialog
                        invoiceId={costOpen ? invoice.id : null}
                        open={costOpen}
                        onOpenChange={setCostOpen}
                      />
                      {/* Invalidação (2B): ação TERMINAL com motivo obrigatório. O backend gateia a permissão INVALIDAR. */}
                      <Button variant="ghost" className="text-destructive hover:text-destructive"
                        onClick={() => setConfirmInvalidateOpen(true)} disabled={busy}>
                        <Ban className="h-4 w-4" /> Invalidar importação
                      </Button>
                    </>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cadastro de fornecedor a partir do emitente da NF (pré-preenchido; o usuário confere e salva). */}
      <Dialog open={supplierDraft !== null} onOpenChange={(o) => { if (!o && !supplierSaving) setSupplierDraft(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cadastrar fornecedor</DialogTitle>
            <DialogDescription>
              Dados vindos do XML da NF-e{profile?.available ? " e da Receita Federal" : ""}. Confira antes de salvar.
            </DialogDescription>
          </DialogHeader>
          {supplierDraft && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sup-name">Nome</Label>
                <Input id="sup-name" value={supplierDraft.name}
                  onChange={(e) => setSupplierDraft({ ...supplierDraft, name: e.target.value })} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="sup-doc">CNPJ / CPF</Label>
                  <Input id="sup-doc" value={supplierDraft.document}
                    onChange={(e) => setSupplierDraft({ ...supplierDraft, document: e.target.value })} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="sup-phone">Telefone</Label>
                  <Input id="sup-phone" value={supplierDraft.phone} placeholder="(00) 00000-0000" inputMode="tel"
                    onChange={(e) => setSupplierDraft({ ...supplierDraft, phone: formatPhone(e.target.value) })} />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sup-notes">Observações</Label>
                <Textarea id="sup-notes" rows={3} value={supplierDraft.notes}
                  onChange={(e) => setSupplierDraft({ ...supplierDraft, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierDraft(null)} disabled={supplierSaving}>Cancelar</Button>
            <Button onClick={() => void saveSupplier()} disabled={supplierSaving || !supplierDraft?.name.trim()}>
              {supplierSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Salvar fornecedor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar esta prévia?</AlertDialogTitle>
            <AlertDialogDescription>
              A prévia sai da lista padrão (consultável pelo filtro “Descartada”). O XML original e a
              auditoria são preservados, e você pode reimportá-la depois. Confirmadas não são afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void discard()}
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmInvalidateOpen}
        onOpenChange={(o) => { setConfirmInvalidateOpen(o); if (!o) setInvalidateReason(""); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invalidar esta importação?</AlertDialogTitle>
            <AlertDialogDescription>
              Ação terminal (sem reversão). A NF sai das operações ativas; XML, vínculos e saldos
              consumidos são preservados, e novos mapeamentos/vínculos ficam bloqueados. Invalidar no
              sistema NÃO cancela a NF na SEFAZ.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="nf-invalidate-reason">Motivo (obrigatório)</Label>
            <Textarea
              id="nf-invalidate-reason"
              rows={3}
              value={invalidateReason}
              onChange={(e) => setInvalidateReason(e.target.value)}
              placeholder="Descreva o motivo da invalidação"
              aria-label="Motivo da invalidação"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!invalidateReason.trim()}
              onClick={() => void invalidate()}
            >
              Invalidar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Popup de custo: pergunta depende da flag (aplicar por padrão × não alterar). */}
      <AlertDialog open={costConfirmOpen} onOpenChange={setCostConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {applyCostFlag ? "Confirma alteração do custo?" : "Tem certeza que não deseja alterar o custo?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {applyCostFlag
                ? "O custo de compra desta NF será aplicado aos produtos/materiais mapeados. Itens com divergência de encargos ou custo mais novo exigem conferência em “Revisar item a item”."
                : "A NF fica sem alterar o custo do catálogo. Você pode aplicar depois em “Revisar item a item” ou marcando a opção “Aplicar custo ao catálogo”."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void onCostConfirm()}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

/**
 * Quem emitiu a NF: o que o XML declara (razão social, fantasia, IE, município/UF), o fornecedor cadastrado que
 * casa pelo documento e, para CNPJ, o perfil cadastral da Receita Federal (BrasilAPI, cache no backend).
 */
interface SupplierDraft {
  name: string;
  document: string;
  phone: string;
  notes: string;
}

function EmitterCard({
  invoice,
  profile,
  profileBusy,
  onRefresh,
  onCreateSupplier,
}: {
  invoice: SupplierInvoice;
  profile: EmitterProfile | null;
  profileBusy: boolean;
  onRefresh: () => void;
  /** Presente só para quem pode criar fornecedor (SUPRIMENTOS.FORNECEDOR.CRIAR). */
  onCreateSupplier?: () => void;
}) {
  const e = invoice.emitter;
  const doc = invoice.emitterDocument ?? invoice.emitterCnpj;
  const isCnpj = !!invoice.emitterCnpj;
  const situacaoAtiva = (profile?.situacaoCadastral ?? "").toUpperCase() === "ATIVA";
  return (
    <div className="rounded-xl border p-4 text-sm">
      {/* Cada bloco declara a FONTE: o XML assinado pelo emitente, o cadastro interno de fornecedores e a
          consulta pública à Receita (BrasilAPI). Nenhum é inferido a partir do outro. */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Emitente <SourceBadge label="XML da NF-e" />
          </div>
          <div className="text-base font-semibold">{emitterDisplayName(e, doc)}</div>
          {e?.tradeName && e.tradeName !== e.name && <div className="text-muted-foreground">{e.tradeName}</div>}
          <div className="mt-1 text-xs text-muted-foreground">
            {isCnpj ? "CNPJ" : "CPF"} {formatDocument(doc)}
            {e?.ie && ` · IE ${e.ie}`}
            {e?.city && ` · ${e.city}${e.uf ? `/${e.uf}` : ""}`}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 text-right text-xs">
          {e?.supplierId ? (
            <>
              <SourceBadge label="Cadastro interno" />
              <Link to="/admin/fornecedores" className="inline-flex items-center gap-1 text-primary hover:underline">
                <Building2 className="h-3.5 w-3.5" /> Fornecedor cadastrado: {e.supplierName}
              </Link>
            </>
          ) : (
            <div className="flex flex-col items-end gap-1 text-muted-foreground">
              <div>Não está no cadastro de fornecedores (procurado pelo {isCnpj ? "CNPJ" : "CPF"})</div>
              {onCreateSupplier ? (
                <Button size="sm" variant="outline" className="gap-1" onClick={onCreateSupplier}>
                  <UserPlus className="h-3.5 w-3.5" /> Cadastrar este emitente
                </Button>
              ) : (
                <Link to="/admin/fornecedores" className="text-primary hover:underline">ver cadastro</Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Receita Federal: só CNPJ tem consulta pública. Para CPF a seção explica o porquê em vez de sumir. */}
      {!isCnpj ? (
        <div className="mt-3 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground" aria-label="Receita Federal">
          <span className="font-medium text-foreground">Receita Federal:</span> não consultada — emitente pessoa física
          (produtor rural, CPF); a consulta pública cadastral existe apenas para CNPJ.
        </div>
      ) : (
        <div className="mt-3 rounded-lg bg-muted/40 p-3" aria-label="Receita Federal">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-medium">
              Receita Federal <SourceBadge label="BrasilAPI" />
              {profile?.available && profile.situacaoCadastral && (
                <Badge variant={situacaoAtiva ? "default" : "destructive"}>{profile.situacaoCadastral}</Badge>
              )}
              {profile?.stale && <Badge variant="outline">desatualizado</Badge>}
              {profile && !profile.available && <Badge variant="destructive">não encontrado</Badge>}
            </div>
            <Button size="sm" variant="ghost" onClick={onRefresh} disabled={profileBusy} aria-label="Atualizar consulta à Receita">
              {profileBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Atualizar
            </Button>
          </div>
          {profileBusy && !profile ? (
            <div className="mt-2 text-xs text-muted-foreground">Consultando…</div>
          ) : profile?.available ? (
            <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Razão social" value={profile.razaoSocial ?? "—"} />
              <Field label="Nome fantasia" value={profile.nomeFantasia ?? "—"} />
              <Field label="Abertura" value={profile.dataInicioAtividade ?? "—"} />
              <Field label="Natureza jurídica" value={profile.naturezaJuridica ?? "—"} />
              <Field label="Porte" value={profile.porte ?? "—"} />
              <Field
                label="CNAE principal"
                value={profile.cnaeFiscal ? `${profile.cnaeFiscal} — ${profile.cnaeFiscalDescricao ?? ""}` : "—"}
              />
              <Field
                label="Endereço"
                value={[profile.logradouro, profile.numero, profile.complemento, profile.bairro].filter(Boolean).join(", ")
                  + (profile.municipio ? ` — ${profile.municipio}/${profile.uf ?? ""}` : "")
                  + (profile.cep ? ` · CEP ${profile.cep}` : "") || "—"}
              />
              <Field
                label="Contato"
                value={[profile.telefone ? formatPhone(profile.telefone) : null, profile.email].filter(Boolean).join(" · ") || "—"}
              />
              <Field label="Consultado em" value={formatDateTimeSP(profile.fetchedAt)} />
              {profile.error && <div className="text-warning sm:col-span-2 lg:col-span-3">{profile.error}</div>}
            </div>
          ) : (
            <div className="mt-2 text-xs text-muted-foreground">
              {profile?.error ?? "Sem dados cadastrais."}
              {profile?.attemptedAt && <> · tentativa em {formatDateTimeSP(profile.attemptedAt)}</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Selo discreto de ORIGEM do dado (de onde a informação veio), para o operador não confundir as fontes. */
function SourceBadge({ label }: { label: string }) {
  return (
    <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground" title={`Fonte: ${label}`}>
      {label}
    </Badge>
  );
}

/**
 * Orienta o preenchimento do "fator": ele é QUANTAS unidades do produto cabem em 1 unidade da NF
 * (ex.: 1 SC = 25 KG → fator 25), com as unidades reais do item e a prévia da conversão. Só ajuda de UI;
 * a conversão efetiva é recalculada no backend ao vincular. Sem produto/unidade escolhidos, degrada para
 * rótulos genéricos em vez de sumir.
 */
function FactorHint({
  uCom,
  targetUnit,
  targetFallback = "un. do produto",
  factor,
  qCom,
}: {
  uCom: string | null;
  targetUnit: string;
  targetFallback?: string;
  factor: string;
  qCom: string | null;
}) {
  const nf = uCom || "un. da NF";
  const tgt = targetUnit || targetFallback;
  const factorNum = Number((factor ?? "").replace(",", "."));
  const qtyNum = Number((qCom ?? "").replace(",", "."));
  const hasPreview = Number.isFinite(factorNum) && factorNum > 0 && Number.isFinite(qtyNum);
  return (
    <div className="text-xs text-muted-foreground">
      <span className="font-medium">Fator:</span> quantos <strong>{tgt}</strong> há em 1 <strong>{nf}</strong>
      {targetUnit && uCom ? ` (ex.: 1 ${nf} de 25 ${tgt} → fator 25)` : ""}.
      {hasPreview && (
        <span className="ml-1 text-foreground">
          {formatQuantity(qCom)} {nf} × {formatQuantity(factor)} = <strong>{formatQuantity(qtyNum * factorNum)} {tgt}</strong>
        </span>
      )}
    </div>
  );
}

/**
 * Explica a regra 1:1 do vínculo: a quantidade é SEMPRE a do item inteiro da NF, e o recebimento escolhido
 * precisa ter EXATAMENTE a quantidade convertida (qCom × fator). Mostra o valor esperado quando o fator está
 * preenchido, para o operador saber qual recebimento casa antes de escolher.
 */
function ExpectedReceivingHint({
  uCom,
  qCom,
  factor,
  targetUnit,
}: {
  uCom: string | null;
  qCom: string | null;
  factor: string;
  targetUnit: string;
}) {
  const nf = uCom || "un. da NF";
  const tgt = targetUnit || "un. do produto";
  const factorNum = Number((factor ?? "").replace(",", "."));
  const qtyNum = Number((qCom ?? "").replace(",", "."));
  const hasExpected = Number.isFinite(factorNum) && factorNum > 0 && Number.isFinite(qtyNum);
  return (
    <div className="text-xs text-muted-foreground">
      Vínculo integral (1:1): o recebimento deve ter exatamente a quantidade convertida do item
      {hasExpected ? (
        <span className="ml-1 text-foreground">
          — <strong>{formatQuantity(qtyNum * factorNum)} {tgt}</strong> ({formatQuantity(qCom)} {nf} × {formatQuantity(factor)}).
        </span>
      ) : (
        <span>. Defina o fator para ver o valor esperado.</span>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function Axis({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="rounded-lg border p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-semibold ${ok ? "text-primary" : "text-destructive"}`}>{ok ? "OK" : "não"}</div>
    </div>
  );
}
